import { TicketOrigin, TicketStatusFilter } from './types';

/**
 * The Print Shop queues and where each one lives (abo-print-shop-menu-split).
 *
 * One table drives the routes, the sidebar, the ticket detail's back link and
 * the legacy `/print-shop` redirect, so a queue cannot be renamed in one place
 * and left behind in another.
 *
 * - Mail on Demand and Books used to be two top tabs of one "Ticket Queue"
 *   page; each now has its own route (ADR-001).
 * - Magazines keeps the route it already had (`/print-shop/rm`); only its
 *   label changed.
 * - The ticket detail stays at `/print-shop/tickets/:orderId` for both.
 */
export const PRINT_SHOP_ROOT_PATH = '/print-shop';

export const PRINT_SHOP_QUEUE_PATHS = {
  mod: '/print-shop/mod',
  book: '/print-shop/books',
  rm: '/print-shop/rm',
} as const;

export type PrintShopQueueKey = keyof typeof PRINT_SHOP_QUEUE_PATHS;

/**
 * The work lanes, then the archive. "All" is always appended after them.
 *
 * The split is the point. Paid, Accepted and In production are what an
 * operator does today; Cancelled and Imported are records of orders that
 * already happened. The historical import puts ~45,000 orders into the last
 * three and FOUR into the first three, so keeping them adjacent but distinct
 * is what stops the archive reading as a backlog.
 */
export const PRODUCTION_AND_ARCHIVE_TABS: TicketStatusFilter[] = [
  'paid',
  'accepted',
  'in_production',
  'shipped',
  'cancelled',
  'imported',
];

/**
 * The lanes BEFORE Paid (book-order-approval ADR-001): orders waiting for the
 * member's approval, and orders whose member asked for changes. Book orders
 * only — a mailing never enters them, so the Mail on Demand queue does not
 * show them.
 */
export const APPROVAL_TABS: TicketStatusFilter[] = [
  'awaiting_approval',
  'changes_requested',
];

/**
 * The lane BEFORE Paid of a book order whose plan includes an Instant Trust
 * Package (itp-mvp): it waits there until the ITP is marked ready. Book orders
 * only, after the approval lanes and before Paid.
 */
export const ITP_TABS: TicketStatusFilter[] = ['itp_pending'];

export interface TicketQueueDefaults {
  /** The status tab a fresh visit opens on. */
  defaultTab: TicketStatusFilter;
  /** The status tabs, in order. "All" is appended after them. */
  tabs: TicketStatusFilter[];
  /**
   * Whether the All tab leaves out the `imported` archive, in its rows (the
   * DS `excludeImported` parameter) and in its badge (`total − imported`).
   * ADR-002: Books' All tab is the live backlog, not 26,000 historical records.
   */
  allExcludesImported: boolean;
}

/**
 * Mail on Demand keeps exactly what the old Ticket Queue did on its tab. Books
 * opens on Paid, the lane where its work starts; its two approval lanes and the
 * Waiting for ITP lane come first, but nothing in them can be printed yet, so
 * they are not where a fresh visit lands.
 */
export const TICKET_QUEUE_DEFAULTS: Record<TicketOrigin, TicketQueueDefaults> =
  {
    mod: {
      defaultTab: 'all',
      tabs: PRODUCTION_AND_ARCHIVE_TABS,
      allExcludesImported: false,
    },
    book: {
      defaultTab: 'paid',
      tabs: [...APPROVAL_TABS, ...ITP_TABS, ...PRODUCTION_AND_ARCHIVE_TABS],
      allExcludesImported: true,
    },
  };

/** The queue page a ticket of this origin belongs to. */
export const queuePathForOrigin = (origin: TicketOrigin): string =>
  PRINT_SHOP_QUEUE_PATHS[origin];

/** Where the bare `/print-shop` lands when nothing says otherwise (ADR-003). */
export const LEGACY_PRINT_SHOP_DEFAULT_PATH = PRINT_SHOP_QUEUE_PATHS.book;

const LEGACY_QUEUE_ALIASES: Record<string, PrintShopQueueKey> = {
  mod: 'mod',
  'mail-on-demand': 'mod',
  mailing: 'mod',
  book: 'book',
  books: 'book',
  rm: 'rm',
  magazine: 'rm',
  magazines: 'rm',
};

/**
 * The Print Shop pages that exist only for Mail on Demand: its queue, the
 * mailing batches (a book order never enters a batch) and the MOD pricing.
 * They are hidden while `VITE_ENABLE_MOD` is off (`env.ENABLE_MOD`).
 */
export const MAIL_ON_DEMAND_ONLY_PATHS: readonly string[] = [
  PRINT_SHOP_QUEUE_PATHS.mod,
  '/print-shop/batches',
  '/print-shop/pricing-config',
];

/**
 * Where an old `/print-shop` link should go (ADR-003). `?tab=` or `?origin=`
 * naming a queue lands on that queue; anything else lands on Books. With
 * Mail on Demand hidden (`modEnabled` false), a link to its queue lands on
 * Books too.
 */
export const resolveLegacyPrintShopPath = (
  search: string,
  modEnabled = true,
): string => {
  const params = new URLSearchParams(search);
  for (const name of ['tab', 'origin']) {
    const value = params.get(name)?.trim().toLowerCase();
    const queue = value ? LEGACY_QUEUE_ALIASES[value] : undefined;
    if (queue && (queue !== 'mod' || modEnabled)) {
      return PRINT_SHOP_QUEUE_PATHS[queue];
    }
  }
  return LEGACY_PRINT_SHOP_DEFAULT_PATH;
};

/**
 * A Print Shop sidebar item is active on its own page and on the pages below
 * it (`/print-shop/rm/:jobId` → Magazines, `/print-shop/batches/:id` →
 * Batches). The trailing slash keeps `/print-shop/books` from matching a
 * hypothetical `/print-shop/booksX`.
 */
export const isPrintShopNavItemActive = (
  route: string,
  pathname: string,
): boolean => pathname === route || pathname.startsWith(`${route}/`);
